import { lastNonNullPoint, stepJoin } from '../hooks/seriesMath';
import {
  dropAfterLastPrice,
  fiscalYearNumber,
  forwardFiscalYears,
  netMarginFromRow,
  reportedFiscalYears,
  scaleTotalSeries,
  statementTimestamp,
  yoyGrowth,
} from './annualGrowth';

/**
 * Quality-score pillar caps. The old calibration — do not refit them.
 * Helper outputs are fractions. The chart multiplies by 100.
 *
 * Revenue quality's 3-year average growth IS clipped, as of 2026-10-29: it
 * reuses FCF's `growthClip` (±0.08) and `growthScale` (1.25) verbatim, and
 * the pillar itself is floored at 0. Both changes are justified on their own
 * terms (a floor stops a single bad year from subtracting; the clip keeps
 * revenue quality consistent with the FCF pillar's own clip) — the legacy
 * Django formula having the same floor and the same clip magnitude is
 * corroborating evidence, not the reason (SCORE_CARDS.md §1.6 — see
 * `revenueQualityPillar`).
 */
export const QUALITY_CAPS = {
  window: 3,
  profitability: {
    max: 0.30,
    clip: 0.15,
    scale: 2.0,
    assetTurnoverDivisor: 10,
  },
  dilution: {
    floor: -0.30,
    saturation: 0.30,
  },
  leverage: {
    max: 0.30,
  },
  fcf: {
    // Generic path 0.25 (no stability terms); financial equity/assets path
    // keeps the pre-2026-11-03 pillar: stability terms, max 0.40.
    max: 0.25,
    financialMax: 0.40,
    growthClip: 0.08,
    growthScale: 1.25,
    growthWeight: 1.5,
    earningsClip: 0.10,
    growthCvCeiling: 1.4,
    levelCvCeiling: 10,
  },
  revenue: {
    // Generic path 0.15 (no stability terms); financial path 0.30 with them.
    max: 0.15,
    financialMax: 0.30,
    growthWeight: 1.5,
    growthCvCeiling: 2.2,
    levelCvCeiling: 15,
  },
  stability: {
    perfect: 0.10,
    weight: 0.75,
  },
  // Consistency pillar (2026-11-03), generic path only: EPS direction over
  // 6 consecutive years (5 YoY changes) and operating-margin stability over
  // 5, mean of the two in [-1, 1], times max. Range [-0.40, 0.40].
  consistency: {
    max: 0.40,
    epsYears: 6,
    marginYears: 5,
    marginInverseCvZero: 2,
    marginInverseCvFull: 10,
    epsWeight: 0.5,
    marginWeight: 0.5,
  },
  quality: {
    min: 0,
    max: 1,
  },
};

const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

const clamp = (value, lo, hi) => Math.min(hi, Math.max(lo, value));

const mean = (values) => values.reduce((sum, value) => sum + value, 0) / values.length;

/** Population standard deviation. Identical values → 0, the ceiling case. */
const populationStdev = (values) => {
  const center = mean(values);
  const variance = values.reduce((sum, value) => sum + (value - center) ** 2, 0) / values.length;
  return Math.sqrt(variance);
};

/**
 * Inverse CV (`mean / std`), clipped to ±`ceiling`, then scaled so a reading
 * at the ceiling contributes `stability.perfect` (0.10) before the 0.75 weight
 * (±0.075 after it). Zero variance is that ceiling: a flat positive window
 * scores +0.075, a flat negative window scores −0.075. A negative inverse CV
 * is floored at the bottom of that scale, not left unbounded. The only window
 * that contributes nothing is one shorter than 3 years.
 */
const stabilityTerm = (values, ceiling) => {
  if (!values || values.length < QUALITY_CAPS.window || !(ceiling > 0)) return null;
  if (!values.every(isFiniteNumber)) return null;

  const center = mean(values);
  const identical = values.every((value) => value === values[0]);
  const std = populationStdev(values);
  let inverseCv;
  if (identical || std === 0) {
    if (center > 0) inverseCv = ceiling;
    else if (center < 0) inverseCv = -ceiling;
    else inverseCv = 0;
  } else {
    inverseCv = center / std;
  }
  if (!isFiniteNumber(inverseCv)) return null;

  const { perfect, weight } = QUALITY_CAPS.stability;
  const clipped = clamp(inverseCv, -ceiling, ceiling);
  return (perfect * clipped / ceiling) * weight;
};

const finiteWindow = (values) => (
  Array.isArray(values)
  && values.length === QUALITY_CAPS.window
  && values.every(isFiniteNumber)
);

/**
 * Profitability, max 0.30.
 * `mean(netMargin, operatingRoa) / 2 + assetTurnover / 10`, clip to
 * [0, 0.15], x 2.0. Missing terms are omitted from the mean, not filled with
 * 0. `operatingRoa` (operatingIncome / totalAssets) comes from the same-year
 * balance sheet; a missing one leaves the pillar on net margin.
 */
export const profitabilityPillar = ({
  netMargin = null, operatingRoa = null, assetTurnover = null,
} = {}) => {
  const ratios = [netMargin, operatingRoa].filter(isFiniteNumber);
  const hasTurnover = isFiniteNumber(assetTurnover);
  if (!ratios.length && !hasTurnover) return null;
  let raw = ratios.length ? mean(ratios) / 2 : 0;
  if (hasTurnover) raw += assetTurnover / QUALITY_CAPS.profitability.assetTurnoverDivisor;
  const { clip, scale } = QUALITY_CAPS.profitability;
  return clamp(raw, 0, clip) * scale;
};

/**
 * Operating-margin guard on the profitability input (funviz-amplify's
 * `houseScores.mjs`, mirrored byte-for-byte — SCORE_CARDS.md §1.6): feeds
 * `min(netMargin, operatingMargin)` into profitability instead of the raw
 * net margin whenever BOTH a real operating income and a positive revenue
 * are available, so a one-off non-operating gain (a lawsuit settlement, an
 * asset sale) cannot inflate the pillar past what the business actually
 * earned operating. `operatingIncome === 0` is treated as MISSING — FMP's
 * "no data" sentinel for this field, never a genuine 0% operating margin —
 * so the guard never fires on it. Only ever LOWERS the input, never raises
 * it: a net margin already at or below the operating margin (e.g. a
 * one-off writedown) passes through unchanged. Returns `netMargin`
 * unchanged whenever `operatingIncome` is missing/zero, `revenue` is not
 * positive, or `netMargin` itself is not finite. Operates on the margin
 * value only — it does not decide whether a year scores at all.
 */
export const operatingMarginGuardedMargin = (netMargin, operatingIncome, revenue) => {
  const hasOperatingIncome = isFiniteNumber(operatingIncome) && operatingIncome !== 0;
  if (!hasOperatingIncome || !(revenue > 0) || !isFiniteNumber(netMargin)) return netMargin;
  const operatingMargin = operatingIncome / revenue;
  return Math.min(netMargin, operatingMargin);
};

/**
 * Dilution, down to −0.30. Share-count increases only, saturated at +30%.
 * Buybacks and flat share counts contribute 0. Returns null when the change
 * cannot be computed.
 */
export const dilutionPillar = (sharesNow, sharesPrior) => {
  if (!isFiniteNumber(sharesNow) || !isFiniteNumber(sharesPrior) || sharesPrior === 0) return null;
  const increase = (sharesNow - sharesPrior) / Math.abs(sharesPrior);
  if (!(increase > 0)) return 0;
  return -Math.min(increase, QUALITY_CAPS.dilution.saturation);
};

/**
 * Leverage, max 0.30. FCF coverage (net debt / FCF) measures debt-service
 * capacity against the cash that actually services debt — the primary
 * justification (SCORE_CARDS.md §1.6/Q3). Its concave, 5x-capped shape
 * matches the legacy formula's Debt/FCF pillar (data_refinery.py), which
 * corroborates that the shape is reasonable but is not proof the exact
 * constants (`3`, `+5`, the 5x cap below) are optimal — they have not been
 * independently retuned against this codebase's own data. Keeps NET DEBT —
 * never legacy's book-debt-via-D/E — as the metric. Takes no price input:
 * this pillar no longer
 * reads `companyValue` at all (the "Net debt / company value" UNDERLYING
 * line is unaffected — it is a separate display series, not this pillar).
 * This is the operating-company branch of `leveragePillar` below; REITs and
 * financials get a sector-specific ratio instead (2026-11-01).
 *
 * Continuous at `netDebt = 0` under either FCF sign: the coverage branch
 * evaluates to the same 0.30 (fcf > 0) / 0 (fcf <= 0) the early-return branch
 * gives.
 *
 * KNOWN CLIFF at `fcf = 0` when `netDebt <= 0`. At `fcf = 0` exactly both
 * paths do return 0, but the net-cash branch is a STEP, not a limit: any
 * positive FCF however small returns the full 0.30, so `leveragePillar(-10, 0)`
 * is 0 while `leveragePillar(-10, 1e-9)` is 0.30 — a 30-point jump across an
 * infinitesimal change. (The `netDebt > 0` seam IS continuous there: FCF → 0⁺
 * sends coverage to the 5× cap, i.e. to 0.) The cliff is deliberately left in
 * place pending a design decision; it is documented, not denied, and the test
 * named for it asserts the step rather than claiming continuity.
 *
 * CONSTANT COUPLING: the `3`, the `+ 5` and `QUALITY_CAPS.leverage.max` are
 * tuned together, and `leverage.max` is overloaded here as the curve's OFFSET
 * rather than as a cap. The curve spans exactly [0, max] only because
 * `3 / (0 + 5) === 2 × 0.30` and `3 / (5 + 5) === 0.30`. Retuning
 * `leverage.max` alone breaks it in both directions — at `max = 0.40` the
 * `netDebt > 0` branch would peak at 0.20 (below the new cap) and bottom out
 * at −0.10 (below zero) while the net-cash early-return above would still hand
 * back 0.40. Any change to `leverage.max` must re-derive the numerator and the
 * coverage-cap offset with it. A separately named offset constant would NOT
 * decouple this: all three numbers move together.
 */
const operatingLeverageCoverage = (netDebt, fcf) => {
  if (!isFiniteNumber(netDebt) || !isFiniteNumber(fcf)) return null;
  if (netDebt <= 0) return fcf > 0 ? QUALITY_CAPS.leverage.max : 0;
  const coverage = fcf > 0 ? Math.min(netDebt / fcf, 5) : 5;
  return 3 / (coverage + 5) - QUALITY_CAPS.leverage.max;
};

// ─── Sector-specific leverage (2026-11-01; bank/insurance/otherFin thresholds
// rebased 2026-11-02, accepted by the owner) ─────────────────────
// Port of funviz-amplify houseScores.mjs's sector-leverage block — keep the two
// byte-for-byte in step (the shared golden fixture pins both). Each helper is a
// linear ramp: the full 0.30 at the "good" threshold, 0 at the "bad" one,
// clamped outside, `null` when its inputs are missing (SCORE_CARDS.md §1.6).
//
//   Sector                         Metric                 Good -> Bad
//   REIT (`isReit`)                net debt / EBITDA*     3.5x -> 7x (2026-11-03; was 4x -> 9x)
//   Bank ("Banks*")                equity / totalAssets   15% -> 10.5%
//   Insurance (non-life)           equity / totalAssets   68.5% -> 21%
//   Life insurance                 equity / totalAssets   30.5% -> 4%
//   Other financial (catch-all)    equity / totalAssets   85% -> 19.5%,
//     or the operating FCF-coverage curve when goodwill + intangibles
//     exceed 50% of equity (or equity is non-positive and goodwill > 0).
//   * REIT EBITDA = 3-year mean of min(FMP ebitda, operatingIncome + D&A),
//     see `reitLeverageEbitda`. A non-positive EBITDA scores 0.
//
// 2026-11-02 REBASE (accepted by the owner). The bank/insurance/
// life/otherFin thresholds launched 2026-11-01 as unchecked textbook anchors
// and let the sector path max the pillar far more often than the generic
// net-debt/FCF curve does (scored live names at the full 0.30: generic 16.4%;
// old bank 36%, non-life 31%, life 31%, otherFin 77%). Owner rule: the special
// path must never make the full 0.30, or a 90+/100 Quality score, easier to
// reach than the generic path, and is STRICT BY OWNER DIRECTIVE: a bucket
// maxes out at most about half as often as the generic curve (16.4%), and its
// share at 0 stays at or below the generic curve's (54.8%; near-universal 0
// would revive the rejected "drop leverage for financials" design). Each
// "good" is ~p95 and each "bad" ~p33 of that bucket's scored live
// equity/assets distribution (~5-6% max out, about a third score 0). Equity/assets is not mapped onto the
// generic curve (no defensible mapping). otherFin no longer shares bank's band.
// Single fiscal year, never smoothed (smoothing hides NYCB's 2023 / ZION's 2022
// capital troughs). Evidence and residuals: funviz-amplify
// `docs/adr/0011-growth-score-margin-cap-and-floor.md`, rebase amendment.
const SECTOR_LEVERAGE_THRESHOLDS = {
  // 2026-11-03 (ADR 0011 amendment): was 4x -> 9x. Strict by owner directive:
  // REIT max-out 5.2% vs generic 12.8%, share at 0 54.6% vs generic 55.2%.
  reit: { good: 3.5, bad: 7 },
  bank: { good: 0.15, bad: 0.105 },
  insurance: { good: 0.685, bad: 0.21 },
  lifeInsurance: { good: 0.305, bad: 0.04 },
  otherFin: { good: 0.85, bad: 0.195 },
};

export const OTHER_FIN_GOODWILL_EQUITY_RATIO = 0.5;

/**
 * The goodwill override's test (houseScores.mjs `isGoodwillHeavy`): goodwill +
 * intangibles above half of positive equity, or any positive goodwill against
 * non-positive equity. Missing (not 0) inputs never fire it.
 */
export const isGoodwillHeavy = (goodwillAndIntangibleAssets, equity) => (
  isFiniteNumber(goodwillAndIntangibleAssets) && isFiniteNumber(equity)
  && (equity > 0
    ? goodwillAndIntangibleAssets / equity > OTHER_FIN_GOODWILL_EQUITY_RATIO
    : goodwillAndIntangibleAssets > 0)
);

const leverageRamp = (value, goodAt, badAt, max = QUALITY_CAPS.leverage.max) => {
  if (!isFiniteNumber(value)) return null;
  if (goodAt >= badAt) {
    if (value >= goodAt) return max;
    if (value <= badAt) return 0;
    return max * (value - badAt) / (goodAt - badAt);
  }
  if (value <= goodAt) return max;
  if (value >= badAt) return 0;
  return max * (badAt - value) / (badAt - goodAt);
};

const equityRatioRamp = (equity, totalAssets, { good, bad }) => {
  if (!isFiniteNumber(equity) || !isPosFinite(totalAssets)) return null;
  return leverageRamp(equity / totalAssets, good, bad);
};

/** REIT: net debt / EBITDA, 3.5x (0.30) -> 7x (0). Non-positive EBITDA scores 0; `null` when either input is missing. */
export const reitLeverageValue = (netDebt, ebitda) => {
  if (!isFiniteNumber(netDebt) || !isFiniteNumber(ebitda)) return null;
  if (ebitda <= 0) return 0;
  const { good, bad } = SECTOR_LEVERAGE_THRESHOLDS.reit;
  return leverageRamp(netDebt / ebitda, good, bad);
};

/** Bank: equity / totalAssets, 15% -> 10.5% (2026-11-02 strict rebase, single fiscal year, never smoothed). */
export const bankLeverageValue = (equity, totalAssets) => (
  equityRatioRamp(equity, totalAssets, SECTOR_LEVERAGE_THRESHOLDS.bank)
);

/** Non-life insurer (P&C, Diversified, Specialty, Reinsurance): equity / totalAssets, 68.5% -> 21% (2026-11-02 strict rebase). */
export const insuranceLeverageValue = (equity, totalAssets) => (
  equityRatioRamp(equity, totalAssets, SECTOR_LEVERAGE_THRESHOLDS.insurance)
);

/** Life insurer: equity / totalAssets, 30.5% -> 4% (2026-11-02 strict rebase). */
export const lifeInsuranceLeverageValue = (equity, totalAssets) => (
  equityRatioRamp(equity, totalAssets, SECTOR_LEVERAGE_THRESHOLDS.lifeInsurance)
);

/**
 * Other financial: equity / totalAssets, 85% -> 19.5% (2026-11-02 strict rebase, no
 * longer shared with bank), UNLESS goodwill + intangibles exceed half of
 * equity — then the operating FCF-coverage curve, authoritatively (no
 * fallback to equity/assets). Non-positive equity with positive goodwill
 * counts as over the threshold (owner decision 2026-11-01: negative equity
 * is the extreme case of book equity that is not meaningful, e.g. MSCI,
 * GSHD FY2025). Missing goodwill or equity cannot trigger the switch.
 */
export const otherFinLeverageValue = ({
  equity = null, totalAssets = null, goodwillAndIntangibleAssets = null, netDebt = null, fcf = null,
  goodwillHeavy = isGoodwillHeavy(goodwillAndIntangibleAssets, equity),
} = {}) => {
  if (goodwillHeavy) {
    return operatingLeverageCoverage(netDebt, fcf);
  }
  return equityRatioRamp(equity, totalAssets, SECTOR_LEVERAGE_THRESHOLDS.otherFin);
};

/**
 * FMP profile `industry` -> leverage bucket for a financial-sector symbol:
 * "Banks*" -> bank; "Insurance - Life" -> life; "Insurance - Brokers" ->
 * other (a broker underwrites no risk); any other "Insurance*" -> insurance;
 * everything else (and a missing industry) -> other.
 */
export const classifyFinancialLeverageSector = (industry) => {
  if (typeof industry !== 'string') return 'other';
  if (/^Banks(\s|-|$)/.test(industry)) return 'bank';
  if (industry === 'Insurance - Life') return 'life';
  if (industry === 'Insurance - Brokers') return 'other';
  if (/^Insurance/.test(industry)) return 'insurance';
  return 'other';
};

/**
 * Leverage pillar dispatcher, max 0.30. `isReit` wins over `isFinancial`;
 * a financial row is bucketed by `industry`; everything else gets the
 * operating-company FCF-coverage curve above, unchanged.
 */
export const leveragePillar = (netDebt, fcf, sector = {}) => {
  const {
    isFinancial = false,
    isReit = false,
    industry = null,
    equity = null,
    totalAssets = null,
    goodwillAndIntangibleAssets = null,
    ebitda = null,
    // Optional routing override (the per-company `latestGoodwillHeavy`);
    // `undefined` falls back to this year's own goodwill test.
    goodwillHeavy = undefined,
  } = sector;
  if (isReit) return reitLeverageValue(netDebt, ebitda);
  if (isFinancial) {
    const bucket = classifyFinancialLeverageSector(industry);
    if (bucket === 'bank') return bankLeverageValue(equity, totalAssets);
    if (bucket === 'insurance') return insuranceLeverageValue(equity, totalAssets);
    if (bucket === 'life') return lifeInsuranceLeverageValue(equity, totalAssets);
    return otherFinLeverageValue({ equity, totalAssets, goodwillAndIntangibleAssets, netDebt, fcf, goodwillHeavy });
  }
  return operatingLeverageCoverage(netDebt, fcf);
};

// ─── Financial equity/assets path: substituted pillars (2026-11-02) ───────
// Port of houseScores.mjs's block of the same name (accepted by the owner
// 2026-11-02; evidence in funviz-amplify ADR 0011's amendment). On a financial
// year scored on the equity/assets leverage path (bank, insurance, life, or
// other financial without the goodwill override): profitability is ROA on a
// per-bucket ramp; FCF quality and revenue quality run on net income (a
// bank's cash flow is loan/deposit/float flows and its revenue is gross
// interest income); the volatility haircut reads net income. Strict by owner
// directive: ROA "good" ~p99, "bad" ~p33 of each bucket's scored last-5-year
// ROA with implausible rows excluded (equity <= 0, assets moving >10x, net
// income exactly 0), so the pillar maxes out about half as often as generic
// profitability (1.9% of years) and is 0 no more often (33.2%). The
// net-income FCF pillar has no FCF-vs-earnings term, so it tops out at 0.30.
export const SECTOR_ROA_THRESHOLDS = {
  bank: { good: 0.026, bad: 0.0093 },
  insurance: { good: 0.183, bad: 0.0135 },
  life: { good: 0.051, bad: 0.0036 },
  other: { good: 0.27, bad: 0.012 },
};

/** Total assets moving more than this factor either way year over year makes ROA null. */
export const FINANCIAL_ASSET_JUMP_LIMIT = 10;

/** True when a financial year is on the equity/assets leverage path (not a REIT, not goodwill-heavy other financial). */
export const onFinancialEquityAssetsPath = ({
  isFinancial = false, isReit = false, industry = null, equity = null, goodwillAndIntangibleAssets = null,
  goodwillHeavy = isGoodwillHeavy(goodwillAndIntangibleAssets, equity),
} = {}) => {
  if (!isFinancial || isReit) return false;
  if (classifyFinancialLeverageSector(industry) !== 'other') return true;
  return !goodwillHeavy;
};

/**
 * Goodwill-override status for routing the whole financial path, leverage
 * included (houseScores.mjs `latestGoodwillHeavy`): the latest year with known
 * equity decides for every year; `false` when no year is known.
 */
export const latestGoodwillHeavy = (rows) => {
  for (let k = rows.length - 1; k >= 0; k -= 1) {
    if (rows[k] && isFiniteNumber(rows[k].equity)) {
      return isGoodwillHeavy(rows[k].goodwillAndIntangibleAssets, rows[k].equity);
    }
  }
  return false;
};

/** Net income as the financial path reads it: an exact 0 is FMP's "no figure", so missing. */
export const financialNetIncome = (row) => (
  row && isFiniteNumber(row.netIncome) && row.netIncome !== 0 ? row.netIncome : null
);

/**
 * Equity/assets-path profitability, max 0.30: net income / total assets on the
 * bucket's ROA ramp. `null` when net income is missing or exactly 0, total
 * assets are not positive, equity is missing or not positive, or total assets
 * moved more than `FINANCIAL_ASSET_JUMP_LIMIT` either way against
 * `priorTotalAssets` (the previous consecutive year's).
 */
export const financialRoaPillar = (netIncome, totalAssets, industry, equity, priorTotalAssets = null) => {
  if (!isFiniteNumber(netIncome) || netIncome === 0 || !isPosFinite(totalAssets) || !isPosFinite(equity)) return null;
  if (isPosFinite(priorTotalAssets)) {
    const move = totalAssets / priorTotalAssets;
    if (move > FINANCIAL_ASSET_JUMP_LIMIT || move < 1 / FINANCIAL_ASSET_JUMP_LIMIT) return null;
  }
  const { good, bad } = SECTOR_ROA_THRESHOLDS[classifyFinancialLeverageSector(industry)];
  return leverageRamp(netIncome / totalAssets, good, bad, QUALITY_CAPS.profitability.max);
};

/**
 * FCF quality, floored at 0. Generic path: growth and FCF-vs-earnings terms
 * only, max 0.25 (the stability terms moved into the consistency pillar,
 * 2026-11-03). Financial equity/assets path (`financialPath`): unchanged, with
 * both stability terms, max 0.40. 0 when FCF is not positive. Null when
 * FCF is missing, or positive but no term in the pillar can be computed.
 * A floor is the more defensible design on its own terms: a single bad year
 * should stop contributing, not actively subtract. Legacy's
 * `fcf_quality.clip(lower=0)` (data_refinery.py:529) has the same floor,
 * which corroborates this but is not the reason for it (SCORE_CARDS.md
 * §1.6).
 */
export const fcfQualityPillar = ({
  fcf = null, growthWindow = null, levelWindow = null, earningsRatioWindow = null, financialPath = false,
} = {}) => {
  if (!isFiniteNumber(fcf)) return null;
  if (!(fcf > 0)) return 0;

  const caps = QUALITY_CAPS.fcf;
  let sum = 0;
  let any = false;

  if (finiteWindow(growthWindow)) {
    const clipped = clamp(mean(growthWindow), -caps.growthClip, caps.growthClip);
    sum += clipped * caps.growthScale * caps.growthWeight;
    any = true;
    if (financialPath) {
      const stability = stabilityTerm(growthWindow, caps.growthCvCeiling);
      if (stability != null) sum += stability;
    }
  }

  // Same bar as the growth and level windows: all three ratios finite.
  // One null year (missing FCF or ~0 earnings) drops the term. Do not average
  // the years that remain, and do not fill the gap with 0.
  if (finiteWindow(earningsRatioWindow)) {
    sum += clamp(mean(earningsRatioWindow), -caps.earningsClip, caps.earningsClip);
    any = true;
  }

  // Generic path: no level-stability term, but a computable one still makes
  // the pillar non-null (0 rather than null), as before 2026-11-03.
  if (finiteWindow(levelWindow)) {
    const stability = stabilityTerm(levelWindow, caps.levelCvCeiling);
    if (stability != null) {
      if (financialPath) sum += stability;
      any = true;
    }
  }

  if (!any) return null;
  return clamp(sum, 0, financialPath ? caps.financialMax : caps.max);
};

/**
 * Revenue quality, floored at 0. Generic path: growth term only, max 0.15
 * (stability terms removed 2026-11-03). Financial equity/assets path
 * (`financialPath`): unchanged, with both stability terms, max 0.30. As of 2026-10-29 the 3-year
 * average growth is clipped to ±8% before the ×1.5 weight, reusing FCF's
 * existing `growthClip` (0.08) and `growthScale` (1.25) constants verbatim
 * for consistency within this module — no new numbers are introduced. Legacy
 * (data_refinery.py lines 539, 545) has the same clip magnitude and the
 * same floor, which corroborates both but is not the reason for either; the
 * ±8% figure has not been independently retuned (SCORE_CARDS.md §1.6).
 * Inverse-CV terms use the same
 * 0.10-before-weight scaling as FCF.
 */
export const revenueQualityPillar = ({ growthWindow = null, levelWindow = null, financialPath = false } = {}) => {
  const caps = QUALITY_CAPS.revenue;
  let sum = 0;
  let any = false;

  if (finiteWindow(growthWindow)) {
    const { growthClip, growthScale } = QUALITY_CAPS.fcf;
    const clipped = clamp(mean(growthWindow), -growthClip, growthClip);
    sum += clipped * growthScale * caps.growthWeight;
    any = true;
    if (financialPath) {
      const stability = stabilityTerm(growthWindow, caps.growthCvCeiling);
      if (stability != null) sum += stability;
    }
  }

  // Generic path: no level-stability term, but a computable one still makes
  // the pillar non-null (0 rather than null), as before 2026-11-03.
  if (finiteWindow(levelWindow)) {
    const stability = stabilityTerm(levelWindow, caps.levelCvCeiling);
    if (stability != null) {
      if (financialPath) sum += stability;
      any = true;
    }
  }

  if (!any) return null;
  return clamp(sum, 0, financialPath ? caps.financialMax : caps.max);
};

/** Sum of the pillars that had data, clipped to [0, 1]. Null when none did. */
export const qualityFromPillars = (pillars = {}) => {
  const values = ['profitability', 'dilution', 'leverage', 'fcf', 'revenue']
    .map((key) => pillars[key])
    .filter(isFiniteNumber);
  if (!values.length) return null;
  const sum = values.reduce((total, value) => total + value, 0);
  // Consistency is added last and never makes a year score on its own.
  const consistency = isFiniteNumber(pillars.consistency) ? pillars.consistency : 0;
  return clamp(sum + consistency, QUALITY_CAPS.quality.min, QUALITY_CAPS.quality.max);
};

/**
 * FCF after stock-based compensation: `fcf - max(sbc, 0)` (a reported SBC
 * credit never adds to FCF). Feeds the FCF-quality pillar and the volatility
 * haircut only; leverage stays on reported FCF. Returns `fcf` unchanged when
 * `sbc` is not a finite number.
 */
export function ownerEarningsFcf(fcf, sbc) {
  if (!isFiniteNumber(fcf) || !isFiniteNumber(sbc)) return fcf;
  return fcf - Math.max(sbc, 0);
}

/**
 * Post-clamp volatility haircut. Identical in funviz-amplify's houseScores.mjs
 * and funviz-react's qualityScore.js (keep the two in sync by hand).
 *
 * Inputs are the TRAILING 5 consecutive fiscal years of revenue and of FCF
 * after SBC. Each series gets a factor in [floor, 1]:
 *   - fewer than 5 consecutive finite values           -> 1 (no haircut)
 *   - FCF only: 5-year mean <= 0                       -> 1 (the FCF pillar
 *     already scores 0, so no second penalty)
 *   - coefficient of variation (pop. std / |mean|) < 0.10 -> 1 (flat series)
 *   - trend stability `seriesStability` (Pearson r vs time) null -> 1
 *   - r >= hi -> 1; r <= lo -> floor; linear in between.
 * Revenue: hi 0.6, lo -0.2, floor 0.70. FCF: hi 0.3, lo -0.5, floor 0.85.
 * The two factors multiply.
 */
export const VOLATILITY_HAIRCUT = {
  window: 5,
  cvExempt: 0.10,
  revenue: { hi: 0.6, lo: -0.2, floor: 0.70 },
  fcf: { hi: 0.3, lo: -0.5, floor: 0.85 },
};

/**
 * Pearson correlation of the series against its index, rounded to 3 dp; null
 * for fewer than 3 points or a flat series. Copy of the screener's
 * metrics.mjs `stability()` (the haircut was simulated against it).
 */
export const seriesStability = (series) => {
  const ys = (series || []).filter(isFiniteNumber);
  if (ys.length < 3) return null;
  const xs = ys.map((_, i) => i);
  const mx = mean(xs);
  const my = mean(ys);
  const num = xs.reduce((a, x, i) => a + (x - mx) * (ys[i] - my), 0);
  const dx = Math.sqrt(xs.reduce((a, x) => a + (x - mx) ** 2, 0));
  const dy = Math.sqrt(ys.reduce((a, y) => a + (y - my) ** 2, 0));
  if (dx === 0 || dy === 0) return null;
  return Math.round((num / (dx * dy)) * 1000) / 1000;
};

const haircutFactor = (series, { hi, lo, floor }, isFcf) => {
  if (!Array.isArray(series) || series.length !== VOLATILITY_HAIRCUT.window || !series.every(isFiniteNumber)) return 1;
  const center = mean(series);
  if (isFcf && !(center > 0)) return 1;
  const cv = center !== 0 ? populationStdev(series) / Math.abs(center) : Infinity;
  if (cv < VOLATILITY_HAIRCUT.cvExempt) return 1;
  const s = seriesStability(series);
  if (s == null || s >= hi) return 1;
  if (s <= lo) return floor;
  return 1 - (1 - floor) * (hi - s) / (hi - lo);
};

export const volatilityHaircut = ({ revenueSeries = null, fcfSeries = null } = {}) => (
  haircutFactor(revenueSeries, VOLATILITY_HAIRCUT.revenue, false)
  * haircutFactor(fcfSeries, VOLATILITY_HAIRCUT.fcf, true)
);

/**
 * Final per-year quality fraction: the pillar sum clamped to [0, 1]
 * (`qualityFromPillars`) times the volatility haircut.
 */
export const finalizeQualityFraction = (pillars, { revenueSeries = null, fcfSeries = null } = {}) => {
  const summed = qualityFromPillars(pillars);
  if (summed == null) return null;
  return summed * volatilityHaircut({ revenueSeries, fcfSeries });
};

// ─── Screener-parity quality engine ───────────────────────────────────────
//
// Port of the quality path of funviz-amplify's
// resources/screener-compute/lib/houseScores.mjs (the row build of
// buildLatestHouseScores, computeYearQualityFraction, averageQualityWindow).
// The Analysis page and the screener run the SAME arithmetic over the SAME raw
// reporting-currency statement rows (`qualityInputs`, served by lambda.py), so
// the two Quality numbers agree by construction. No FX, no price, no
// per-share basis enters the score. Keep in sync with houseScores.mjs by hand;
// tests/screener-quality-parity.test.ts pins both to one golden fixture.

export const QUALITY_LEVERAGE_FCF_WINDOW = 3;
export const QUALITY_AVERAGE_WINDOWS = { qualityScore: 3, qualityScore5y: 5, qualityScore10y: 10 };

const round = (x, dp) => {
  if (!Number.isFinite(x)) return x;
  const f = 10 ** dp;
  return Math.round(x * f) / f;
};

const isPosFinite = (x) => Number.isFinite(x) && x > 0;

/** Strict YoY growth (current - prior) / |prior|; null on a missing or zero prior. */
// Strict zero test (prior === 0), unlike annualGrowth's yoyGrowth, to match the screener exactly.
const yoyGrowthStrict = (current, prior) => {
  if (!isFiniteNumber(current)) return null;
  if (!isFiniteNumber(prior) || prior === 0) return null;
  return (current - prior) / Math.abs(prior);
};

/** True iff fiscal years `i` and `j` are adjacent (rows[i].year === rows[j].year + 1). */
const consecutiveYears = (rows, i, j) => (
  i >= 0 && j >= 0 && i < rows.length && j < rows.length
  && rows[i].year != null && rows[j].year != null && rows[i].year === rows[j].year + 1
);

const threeConsecutiveAt = (rows, i) => (
  i >= 2 && consecutiveYears(rows, i, i - 1) && consecutiveYears(rows, i - 1, i - 2)
);

const growthAtYear = (rows, pick, i) => (
  consecutiveYears(rows, i, i - 1) ? yoyGrowthStrict(pick(rows[i]), pick(rows[i - 1])) : null
);

const fcfAfterSbc = (row) => ownerEarningsFcf(row.fcf, row.sbc);

/** Mean reported FCF over the `windowSize` consecutive years ending at `i`, or null (caller falls back to that year's own FCF). */
function fcfWindowMean(rows, i, windowSize) {
  const firstIndex = i - windowSize + 1;
  if (firstIndex < 0) return null;
  for (let k = i; k > firstIndex; k--) {
    if (!consecutiveYears(rows, k, k - 1)) return null;
  }
  const slice = rows.slice(firstIndex, i + 1).map((r) => r.fcf);
  if (!slice.every(isFiniteNumber)) return null;
  return mean(slice);
}

/** Mean of `pick(row)` over the `windowSize` consecutive years ending at `i`, or null (caller falls back to the single year). */
function consecutiveWindowMean(rows, i, windowSize, pick) {
  const firstIndex = i - windowSize + 1;
  if (firstIndex < 0) return null;
  for (let k = i; k > firstIndex; k--) {
    if (!consecutiveYears(rows, k, k - 1)) return null;
  }
  const slice = rows.slice(firstIndex, i + 1).map(pick);
  if (!slice.every(isFiniteNumber)) return null;
  return mean(slice);
}

// REIT leverage's EBITDA, the ONE place it is defined (houseScores.mjs
// `reitLeverageEbitda`, ADR 0011): per year min(FMP ebitda, operatingIncome +
// D&A) — drops one-off sale gains FMP books into ebitda, keeps impairments —
// using whichever exists when only one does; then the 3-year consecutive mean,
// else the single year. An exact-0 ebitda or operatingIncome is MISSING (FMP
// writes 0 for "no figure": EFC/MFA ebitda, CIM operatingIncome); D&A 0 is real.
const reitEbitdaOfYear = (row) => {
  const reported = isFiniteNumber(row.ebitda) && row.ebitda !== 0 ? row.ebitda : null;
  const operating = isFiniteNumber(row.operatingIncome) && row.operatingIncome !== 0
    && isFiniteNumber(row.depreciationAndAmortization)
    ? row.operatingIncome + row.depreciationAndAmortization
    : null;
  if (reported == null) return operating;
  if (operating == null) return reported;
  return Math.min(reported, operating);
};

export const reitLeverageEbitda = (rows, i) => (
  consecutiveWindowMean(rows, i, QUALITY_LEVERAGE_FCF_WINDOW, reitEbitdaOfYear)
    ?? (rows[i] ? reitEbitdaOfYear(rows[i]) : null)
);

/** Operating-margin guard, screener arithmetic: min(netMargin, operatingIncome / revenue) when both inputs exist; otherwise netMargin. */
const guardedMargin = (row) => operatingMarginGuardedMargin(row.margin, row.operatingIncome, row.revenue);

/** True iff the `windowSize` fiscal years ending at `lastIndex` are consecutive. */
const isConsecutiveWindow = (rows, lastIndex, windowSize) => {
  const firstIndex = lastIndex - windowSize + 1;
  if (firstIndex < 0) return false;
  for (let k = lastIndex; k > firstIndex; k--) {
    if (!consecutiveYears(rows, k, k - 1)) return false;
  }
  return true;
};

// ─── Consistency pillar (2026-11-03, owner-approved) ───────────────────────
//
// Generic path only (null on the financial equity/assets path). Rewards a
// business whose earnings per share rise year after year on steady operating
// margins. Two components, each in [-1, 1]:
//   e = EPS direction over the 6 consecutive fiscal years ending at i:
//       2 × up / 5 − 1, where `up` counts the 5 YoY changes with EPS not
//       falling AND EPS > 0. EPS = netIncome / weightedAverageShsOut.
//   m = operating-margin stability over the 5 consecutive years ending at i:
//       inverse CV (mean / population std) ramped 0 at 2 to 1 at 10; a
//       negative mean is −1; a zero std is the sign of the mean.
// Either component null (too few consecutive years, or a missing input in its
// window) -> the pillar is 0. Else 0.40 × clamp(0.5e + 0.5m, −1, 1).
// Evidence: funviz-amplify docs/adr/0015-quality-consistency-pillar.md.
// Byte-for-byte shared between houseScores.mjs and qualityScore.js.

const epsOfRow = (row) => (
  isFiniteNumber(row.netIncome) && row.shares > 0 ? row.netIncome / row.shares : null
);

export const consistencyEpsComponent = (rows, i) => {
  const { epsYears } = QUALITY_CAPS.consistency;
  if (!isConsecutiveWindow(rows, i, epsYears)) return null;
  const eps = rows.slice(i - epsYears + 1, i + 1).map(epsOfRow);
  if (!eps.every(isFiniteNumber)) return null;
  let up = 0;
  for (let k = 1; k < eps.length; k++) {
    if (eps[k] >= eps[k - 1] && eps[k] > 0) up++;
  }
  const share = up / (eps.length - 1);
  return 2 * share - 1;
};

export const consistencyMarginComponent = (rows, i) => {
  const { marginYears, marginInverseCvZero, marginInverseCvFull } = QUALITY_CAPS.consistency;
  if (!isConsecutiveWindow(rows, i, marginYears)) return null;
  const margins = rows.slice(i - marginYears + 1, i + 1).map((row) => (
    isFiniteNumber(row.operatingIncome) && row.revenue > 0 ? row.operatingIncome / row.revenue : null
  ));
  if (!margins.every(isFiniteNumber)) return null;
  const center = mean(margins);
  const std = populationStdev(margins);
  if (std === 0) {
    if (center > 0) return 1;
    return center < 0 ? -1 : 0;
  }
  const inverseCv = center / std;
  if (inverseCv < 0) return -1;
  return clamp((inverseCv - marginInverseCvZero) / (marginInverseCvFull - marginInverseCvZero), 0, 1);
};

export const consistencyPillar = (rows, i) => {
  const e = consistencyEpsComponent(rows, i);
  const m = consistencyMarginComponent(rows, i);
  if (e == null || m == null) return 0;
  const { max, epsWeight, marginWeight } = QUALITY_CAPS.consistency;
  return clamp(epsWeight * e + marginWeight * m, -1, 1) * max;
};

/**
 * Screener row build: one row per income statement, joined to its own
 * cash-flow and balance-sheet rows by EXACT statement date. Input arrays are
 * oldest-first raw rows (see lambda.py's build_quality_inputs). `year` follows
 * the screener's rule: fiscalYear, else the statement date's year.
 */
export function buildScoreRows({ income = [], cashflow = [], balance = [] } = {}) {
  const cashflowByDate = new Map((cashflow || []).map((r) => [r.date, r]));
  const balanceByDate = new Map((balance || []).map((r) => [r.date, r]));
  return (income || [])
    .filter((r) => r && typeof r.date === 'string')
    .map((row) => {
      const cf = cashflowByDate.get(row.date) || null;
      const bs = balanceByDate.get(row.date) || null;
      const revenue = Number.isFinite(row.revenue) ? row.revenue : null;
      const netIncome = Number.isFinite(row.netIncome) ? row.netIncome : null;
      const operatingIncome = Number.isFinite(row.operatingIncome) ? row.operatingIncome : null;
      const shares = isPosFinite(row.weightedAverageShsOut) ? row.weightedAverageShsOut : null;
      const totalAssets = Number.isFinite(bs?.totalAssets) ? bs.totalAssets : null;
      const year = Number.parseInt(row.fiscalYear ?? row.date.slice(0, 4), 10);
      return {
        date: row.date,
        x: statementTimestamp(row),
        year: Number.isFinite(year) ? year : null,
        revenue,
        netIncome,
        margin: isPosFinite(revenue) && netIncome != null ? netIncome / revenue : null,
        operatingIncome,
        // Operating ROA, screener's operatingRoa(): null for a zero/missing
        // operating income or a non-positive total-assets figure.
        operatingRoa: operatingIncome != null && operatingIncome !== 0 && isPosFinite(totalAssets)
          ? operatingIncome / totalAssets
          : null,
        ebitda: Number.isFinite(row.ebitda) ? row.ebitda : null,
        // Growth lender gate and net revenue (2026-11-03, `isGrowthLender`).
        interestIncome: Number.isFinite(row.interestIncome) ? row.interestIncome : null,
        interestExpense: Number.isFinite(row.interestExpense) ? row.interestExpense : null,
        // Sector-leverage inputs (2026-11-01), ending balance sheet.
        // An exact-0 equity is FMP's "no figure", so missing.
        equity: Number.isFinite(bs?.totalStockholdersEquity) && bs.totalStockholdersEquity !== 0
          ? bs.totalStockholdersEquity
          : null,
        totalAssets,
        goodwillAndIntangibleAssets: Number.isFinite(bs?.goodwillAndIntangibleAssets)
          ? bs.goodwillAndIntangibleAssets
          : null,
        fcf: Number.isFinite(cf?.freeCashFlow) ? cf.freeCashFlow : null,
        sbc: Number.isFinite(cf?.stockBasedCompensation) ? cf.stockBasedCompensation : null,
        // REIT inputs (2026-11-01): OCF is the Growth margin basis, D&A feeds
        // the leverage EBITDA (`reitLeverageEbitda`).
        operatingCashFlow: Number.isFinite(cf?.operatingCashFlow) ? cf.operatingCashFlow : null,
        depreciationAndAmortization: Number.isFinite(cf?.depreciationAndAmortization)
          ? cf.depreciationAndAmortization
          : null,
        shares,
        netDebt: Number.isFinite(bs?.totalDebt) && Number.isFinite(bs?.cashAndCashEquivalents)
          ? bs.totalDebt - bs.cashAndCashEquivalents
          : null,
      };
    });
}

/**
 * Earnings the FCF-quality pillar's FCF-vs-earnings term compares FCF with.
 * Net income for everyone except a REIT, which compares against net income +
 * D&A, an FFO proxy (not FFO): REIT net income is after real-estate depreciation
 * that capex does not match, so FCF reads a median of ~1.6x net income (equity REITs, NI > 0) and the
 * term sat at its +0.10 clip in 86% of REIT-years (85.7% equity, 88.6% mortgage;
 * generic 54%). A missing D&A adds nothing. Port of
 * houseScores.mjs `fcfEarningsBasis` (2026-11-03, ADR 0011 amendment).
 */
function fcfEarningsBasis(row, isReit) {
  if (!isReit || !isFiniteNumber(row.netIncome)) return row.netIncome;
  return row.netIncome + (isFiniteNumber(row.depreciationAndAmortization) ? row.depreciationAndAmortization : 0);
}

/**
 * Per-fiscal-year pillars and 0-1 fraction at index `i` (screener's
 * computeYearQualityFraction). Null when the index has no row.
 */
export function computeYearQualityPillars(rows, i, { isFinancial = false, isReit = false, industry = null } = {}) {
  const row = rows[i];
  if (!row) return null;
  const prevRow = rows[i - 1] || null;
  const hasPrevYear = consecutiveYears(rows, i, i - 1);
  const threeConsecutive = threeConsecutiveAt(rows, i);
  const fcfForLeverage = fcfWindowMean(rows, i, QUALITY_LEVERAGE_FCF_WINDOW) ?? row.fcf;
  const haircutRows = isConsecutiveWindow(rows, i, VOLATILITY_HAIRCUT.window)
    ? rows.slice(i - VOLATILITY_HAIRCUT.window + 1, i + 1)
    : null;

  // Financial equity/assets path (2026-11-02): profitability, FCF quality,
  // revenue quality and the haircut read net income (see the block after
  // `leveragePillar`).
  // One routing rule for the whole financial path, leverage included.
  const companyGoodwillHeavy = latestGoodwillHeavy(rows);
  const financialPath = onFinancialEquityAssetsPath({
    isFinancial, isReit, industry, goodwillHeavy: companyGoodwillHeavy,
  });
  const netIncomeOf = financialNetIncome;
  const netIncomeGrowthWindow = threeConsecutive
    ? [growthAtYear(rows, netIncomeOf, i - 2), growthAtYear(rows, netIncomeOf, i - 1), growthAtYear(rows, netIncomeOf, i)]
    : null;
  const netIncomeWindow3 = threeConsecutive ? [rows[i - 2], rows[i - 1], row].map(netIncomeOf) : null;

  const pillars = {
    profitability: financialPath
      ? financialRoaPillar(row.netIncome, row.totalAssets, industry, row.equity, hasPrevYear ? prevRow.totalAssets : null)
      : profitabilityPillar({ netMargin: guardedMargin(row), operatingRoa: row.operatingRoa }),
    dilution: hasPrevYear ? dilutionPillar(row.shares, prevRow.shares) : null,
    leverage: leveragePillar(row.netDebt, fcfForLeverage, {
      isFinancial,
      isReit,
      industry,
      equity: row.equity,
      totalAssets: row.totalAssets,
      goodwillAndIntangibleAssets: row.goodwillAndIntangibleAssets,
      ebitda: isReit ? reitLeverageEbitda(rows, i) : null,
      goodwillHeavy: companyGoodwillHeavy,
    }),
    fcf: financialPath ? fcfQualityPillar({
      fcf: netIncomeOf(row), growthWindow: netIncomeGrowthWindow, levelWindow: netIncomeWindow3, earningsRatioWindow: null, financialPath: true,
    }) : fcfQualityPillar({
      fcf: fcfAfterSbc(row),
      growthWindow: threeConsecutive
        ? [
          growthAtYear(rows, fcfAfterSbc, i - 2),
          growthAtYear(rows, fcfAfterSbc, i - 1),
          growthAtYear(rows, fcfAfterSbc, i),
        ]
        : null,
      levelWindow: threeConsecutive
        ? [fcfAfterSbc(rows[i - 2]), fcfAfterSbc(rows[i - 1]), fcfAfterSbc(row)]
        : null,
      earningsRatioWindow: threeConsecutive
        ? [i - 2, i - 1, i].map((k) => {
          const item = rows[k];
          const itemFcf = fcfAfterSbc(item);
          const earnings = fcfEarningsBasis(item, isReit);
          if (!isFiniteNumber(itemFcf) || !isFiniteNumber(earnings) || earnings === 0) return null;
          return (itemFcf - earnings) / Math.abs(earnings);
        })
        : null,
    }),
    revenue: financialPath
      ? revenueQualityPillar({ growthWindow: netIncomeGrowthWindow, levelWindow: netIncomeWindow3, financialPath: true })
      : revenueQualityPillar({
        growthWindow: threeConsecutive
          ? [
            growthAtYear(rows, (r) => r.revenue, i - 2),
            growthAtYear(rows, (r) => r.revenue, i - 1),
            growthAtYear(rows, (r) => r.revenue, i),
          ]
          : null,
        levelWindow: threeConsecutive ? [rows[i - 2].revenue, rows[i - 1].revenue, row.revenue] : null,
      }),
    // Generic path only; see consistencyPillar.
    consistency: financialPath ? null : consistencyPillar(rows, i),
  };
  return {
    pillars,
    fraction: finalizeQualityFraction(pillars, {
      revenueSeries: haircutRows ? haircutRows.map(financialPath ? netIncomeOf : (r) => r.revenue) : null,
      fcfSeries: haircutRows ? haircutRows.map(financialPath ? netIncomeOf : fcfAfterSbc) : null,
    }),
  };
}

/**
 * Strict trailing `windowSize`-year average (screener's averageQualityWindow),
 * 0-100, rounded to 2dp. Null unless the window is consecutive and every year
 * in it has a score. Averages the already-rounded per-year scores.
 */
export function averageQualityWindow(rows, lastIndex, windowSize, ctx = {}) {
  if (!isConsecutiveWindow(rows, lastIndex, windowSize)) return null;
  const perYear = [];
  for (let k = lastIndex - windowSize + 1; k <= lastIndex; k++) {
    const fraction = computeYearQualityPillars(rows, k, ctx)?.fraction;
    if (!isFiniteNumber(fraction)) return null;
    perYear.push(round(fraction * 100, 2));
  }
  return round(mean(perYear), 2);
}

/** Sector context the per-year engine reads from lambda.py's `qualityInputs`. */
const qualityContext = (qualityInputs) => ({
  isFinancial: !!qualityInputs?.isFinancial,
  isReit: !!qualityInputs?.isReit,
  industry: typeof qualityInputs?.industry === 'string' ? qualityInputs.industry : null,
});

/**
 * The three headline Quality scores (0-100, or null) for the latest reported
 * fiscal year: strict 3/5/10-year averages. Same values the screener stores as
 * qualityScore / qualityScore5y / qualityScore10y.
 */
export function buildQualityHeadlines(qualityInputs = {}) {
  const rows = buildScoreRows(qualityInputs);
  const ctx = qualityContext(qualityInputs);
  if (rows.length < 2) {
    return { qualityScore: null, qualityScore5y: null, qualityScore10y: null };
  }
  const last = rows.length - 1;
  return {
    qualityScore: averageQualityWindow(rows, last, QUALITY_AVERAGE_WINDOWS.qualityScore, ctx),
    qualityScore5y: averageQualityWindow(rows, last, QUALITY_AVERAGE_WINDOWS.qualityScore5y, ctx),
    qualityScore10y: averageQualityWindow(rows, last, QUALITY_AVERAGE_WINDOWS.qualityScore10y, ctx),
  };
}

/**
 * EBITDA margin = EBITDA / revenue. Null when revenue is missing or not
 * positive, or EBITDA is missing. Negative EBITDA is a real reading, not a gap;
 * an exact-0 EBITDA is a gap (FMP writes 0 when it has no figure — AMSF FY2023,
 * MSIF FY2022-23), the same convention as houseScores.mjs.
 *
 * Both inputs must come from the SAME source. `metrics.*` is FX-converted onto
 * the price currency while `incomeStatement.annual[]` stays in the reporting
 * currency, so an EBITDA from one divided by a revenue from the other is the
 * margin multiplied by an FX rate — a plausible-looking wrong number.
 * `buildAnnualScores` takes both from `metrics.*`, where the common conversion
 * factor cancels.
 */
export const ebitdaMarginFrom = (ebitda, revenue) => {
  if (!isFiniteNumber(ebitda) || ebitda === 0 || !isFiniteNumber(revenue) || !(revenue > 0)) return null;
  return ebitda / revenue;
};

/** Operating-cash-flow margin = OCF / revenue — the Growth margin basis for a REIT. Same gap rules as `ebitdaMarginFrom`. */
export const ocfMarginFrom = (operatingCashFlow, revenue) => {
  if (!isFiniteNumber(operatingCashFlow) || !isFiniteNumber(revenue) || !(revenue > 0)) return null;
  return operatingCashFlow / revenue;
};

/**
 * Classic Rule of 40 = EBITDA margin + revenue YoY. Floored at 0, no ceiling.
 * Not a quality pillar, and since 2026-11-02 not what the Growth score plots
 * (see `growthYearScore`): the `ruleOf40Total`/`PerShare` series keep the
 * plain reading for reference. Null when either input is missing.
 *
 * The margin term is an EBITDA margin, which is Brad Feld's original basis.
 * Reported years use the provider's GAAP-derived `ebitda` (EBIT + D&A, so stock
 * comp is still an expense). Forward years do NOT use the EBITDA consensus,
 * which is an analyst adjusted figure on a different basis — they hold this
 * margin flat (see `buildAnnualScores`). Net margin is still the quality
 * score's profitability input — only this term moved.
 */
export const ruleOf40 = (margin, revenueGrowth) => {
  if (!isFiniteNumber(margin) || !isFiniteNumber(revenueGrowth)) return null;
  return Math.max(0, margin + revenueGrowth);
};

// 3Y dropped 2026-10-30: the plotted Quality line IS now the trailing 3Y
// average (rule #3, N=3 — see `buildAnnualScores`), so a 3Y tooltip entry
// would just repeat the hovered point's own y-value. 5Y/10Y stay, computed
// from the same RAW per-year score this average entry lists — see the
// `detail` assignment in `buildAnnualScores`, which passes the raw array,
// never the now-averaged plotted line (no cascading/nested averaging, same
// as funviz-amplify's qualityScore5y/10y, each built from one shared
// per-year score).
const AVERAGE_WINDOWS = [5, 10];

// ─── Growth score: per-year rule and 3-year line (2026-11-02, amended 2026-11-03) ─
// Mirrors funviz-amplify houseScores.mjs's computeYearGrowthScores,
// growthYearScore and growth headline byte for byte (SCORE_CARDS §1.7; ADR
// 0012 in funviz-amplify, which supersedes ADR 0011's Growth formula). Per
// fiscal year with a consecutive prior year:
//  - g = YoY revenue growth; the Total line takes min(total, per-share) growth
//    (per share alone when that is the line);
//  - a positive g is multiplied by the above-peak share of the year's increase
//    over the highest total revenue of the prior GROWTH_PEAK_WINDOW (5)
//    consecutive years (growthPeakFactor): only growth to a new high counts,
//    a recovery toward an old peak earns nothing;
//  - and by growthTrendFactor(risingYearShare): the share of the last
//    GROWTH_TREND_WINDOW (10) YoY revenue changes that did not fall, ramped
//    from 0 at 0.4 to 1 at 0.8 (1 with fewer than 5 changes); a forward year
//    reuses the last reported year's share;
//  - the credited growth is capped at GROWTH_GROWTH_CAP;
//  - m = that year's own margin (EBITDA margin; OCF margin for a REIT; 0 for
//    a lender, whose revenue everywhere here is `lenderNetRevenue`),
//    clamped to [GROWTH_MARGIN_FLOOR, GROWTH_MARGIN_CAP];
//  - score = min(clamp((g_eff + 0.75 m) / 0.70, 0, 1) × 100,
//    clamp(g_eff / GROWTH_CEILING_GROWTH, 0, 1) × 100), 2 dp — the growth
//    ceiling (15%) caps a slow grower in proportion, 0% or less at 0;
//  - a year whose own or base-year revenue is ≤ 0 (exact 0 included) scores 0
//    on both lines; a missing revenue or a gap year is null.
// The plotted line is the strict trailing GROWTH_HEADLINE_WINDOW (3)-year
// average of those per-year scores, capped at 30 when a revenue in the window
// or its base year is ≤ 0.
export const GROWTH_HEADLINE_WINDOW = 3;
export const GROWTH_PEAK_WINDOW = 5;
export const GROWTH_TREND_WINDOW = 10;
export const GROWTH_TREND_MIN_CHANGES = 5;
export const GROWTH_TREND_ZERO_SHARE = 0.4;
export const GROWTH_TREND_FULL_SHARE = 0.8;
export const GROWTH_GROWTH_CAP = 1.0;
export const GROWTH_MARGIN_WEIGHT = 0.75;
export const GROWTH_MARGIN_CAP = 0.40;
export const GROWTH_MARGIN_FLOOR = -1.0;
export const GROWTH_FULL_SCORE = 0.70;
export const GROWTH_CEILING_GROWTH = 0.15;
export const GROWTH_NON_POSITIVE_REVENUE_CAP = 30;

// Lenders (2026-11-03, houseScores.mjs `isGrowthLender`/`lenderNetRevenue`,
// ADR 0012 amendment). A financial, non-REIT company whose LATEST fiscal year
// has interest income ≥ 50% of revenue scores Growth on revenue net of
// interest expense (gross interest income rides the rate cycle), with margin 0
// (bank EBITDA / revenue is not like-for-like). A year with missing or
// exactly-0 interest expense has no net revenue (a gap). No forward Growth
// points: the consensus is on gross revenue.
export const GROWTH_LENDER_INTEREST_SHARE = 0.5;
export const isGrowthLender = (rows, isFinancial, isReit) => {
  const row = rows[rows.length - 1];
  return !!isFinancial && !isReit && !!row && row.revenue > 0 && isFiniteNumber(row.interestIncome)
    && row.interestIncome / row.revenue >= GROWTH_LENDER_INTEREST_SHARE;
};
export const lenderNetRevenue = (row) => (
  row && isFiniteNumber(row.revenue) && isFiniteNumber(row.interestExpense) && row.interestExpense > 0
    ? row.revenue - row.interestExpense : null);

/**
 * Above-peak share of a year's revenue increase (0..1), houseScores.mjs
 * `growthPeakFactor`: 1 when the base year is the peak, (revenue − peak) /
 * (revenue − base) when the year ends above a higher earlier peak, 0 when it
 * ends at or below it, and 0 when base or peak is not positive or revenue is
 * missing. Times total growth this is max(0, revenue − peak) / base.
 */
export const growthPeakFactor = (revenue, base, peak) => {
  if (!(peak > 0 && base > 0)) return 0;
  if (base >= peak) return 1;
  if (isFiniteNumber(revenue) && revenue > peak) return (revenue - peak) / (revenue - base);
  return 0;
};

/**
 * Share (0..1) of the last GROWTH_TREND_WINDOW consecutive YoY changes of
 * `values` ending at index `i` in which the value did not fall
 * (houseScores.mjs `risingYearShare`). `isConsecutiveAt(k)` is true when
 * index k directly follows k − 1. The walk stops at a gap year or a missing
 * or exactly-0 value; null when fewer than GROWTH_TREND_MIN_CHANGES remain.
 */
export const risingYearShare = (values, isConsecutiveAt, i) => {
  let rising = 0;
  let changes = 0;
  for (let k = i; k > i - GROWTH_TREND_WINDOW && isConsecutiveAt(k); k--) {
    const prior = values[k - 1];
    const current = values[k];
    if (!isFiniteNumber(prior) || !isFiniteNumber(current) || prior === 0 || current === 0) break;
    changes++;
    if (current >= prior) rising++;
  }
  return changes >= GROWTH_TREND_MIN_CHANGES ? rising / changes : null;
};

/**
 * Growth-credit multiplier from `risingYearShare` (houseScores.mjs
 * `growthTrendFactor`): 0 at or below GROWTH_TREND_ZERO_SHARE, 1 at or above
 * GROWTH_TREND_FULL_SHARE, linear in between; 1 when the share is null.
 */
export const growthTrendFactor = (share) => (
  share == null
    ? 1
    : clamp((share - GROWTH_TREND_ZERO_SHARE) / (GROWTH_TREND_FULL_SHARE - GROWTH_TREND_ZERO_SHARE), 0, 1)
);

/**
 * One year's Growth score, 0-100 rounded to 2 dp, or null when growth or
 * margin is missing — houseScores.mjs `growthYearScore`, same arithmetic.
 */
export const growthYearScore = (growth, peakFactor, margin) => {
  if (!isFiniteNumber(growth) || !isFiniteNumber(margin) || !isFiniteNumber(peakFactor)) return null;
  const effectiveGrowth = growth > 0 ? Math.min(growth * peakFactor, GROWTH_GROWTH_CAP) : growth;
  const marginTerm = GROWTH_MARGIN_WEIGHT * clamp(margin, GROWTH_MARGIN_FLOOR, GROWTH_MARGIN_CAP);
  const raw = clamp((effectiveGrowth + marginTerm) / GROWTH_FULL_SCORE, 0, 1) * 100;
  // Growth ceiling: below GROWTH_CEILING_GROWTH (15%) the score is capped in
  // proportion (5% -> 33.3, 10% -> 66.7); zero or negative growth caps it at 0.
  let ceiling;
  if (effectiveGrowth <= 0) ceiling = 0;
  else if (effectiveGrowth >= GROWTH_CEILING_GROWTH) ceiling = 100;
  else ceiling = (100 * effectiveGrowth) / GROWTH_CEILING_GROWTH;
  return round(Math.min(raw, ceiling), 2);
};

/**
 * Highest `values[k]` over the GROWTH_PEAK_WINDOW consecutive years before
 * index `i` (houseScores.mjs `priorPeak`). `isConsecutiveAt(k)` is true when
 * index k directly follows k − 1. Null when year i − 1 is not consecutive or
 * its value is missing or exactly 0; a 0 further back is skipped.
 */
const priorPeak = (values, isConsecutiveAt, i) => {
  if (!isConsecutiveAt(i)) return null;
  const base = values[i - 1];
  // `base === 0` is almost dead code: `yearGrowthScores`' own ≤0-revenue
  // early return already covers every case where this year's revenue is ALSO
  // finite. It only still fires when this year's own revenue is missing
  // while the base year's is exactly 0 — and the final score is null either
  // way in that case. Left in as this function's own documented contract.
  if (!isFiniteNumber(base) || base === 0) return null;
  let peak = base;
  for (let j = 2; j <= GROWTH_PEAK_WINDOW && isConsecutiveAt(i - j + 1); j++) {
    const value = values[i - j];
    if (isFiniteNumber(value) && value !== 0) peak = Math.max(peak, value);
  }
  return peak;
};

/**
 * Strict trailing `n`-year mean of per-year growth scores, the screener's
 * averageGrowthWindow: each point's 0-100 score (2 dp, recovered exactly from
 * the stored fraction), averaged, capped at GROWTH_NON_POSITIVE_REVENUE_CAP
 * when a point's revenue or the first point's base-year revenue is not
 * positive, rounded to 2 dp, returned as a fraction. Null unless the last `n`
 * points are `n` consecutive fiscal years.
 */
const trailingGrowthAverage = (points, n) => {
  if (!points || points.length < n) return null;
  const window = points.slice(-n);
  if (window[n - 1].year - window[0].year !== n - 1) return null;
  let average = mean(window.map((point) => round(point.y * 100, 2)));
  const revenues = [window[0].baseRevenue, ...window.map((point) => point.revenue)];
  if (revenues.some((value) => !(value > 0))) average = Math.min(average, GROWTH_NON_POSITIVE_REVENUE_CAP);
  return round(average, 2) / 100;
};

/** Growth-line tooltip: the strict 5Y/10Y `trailingGrowthAverage`s ending at the last point, "—" when not covered. */
export const formatGrowthAverages = (points) => AVERAGE_WINDOWS
  .map((n) => {
    const avg = trailingGrowthAverage(points, n);
    return `${n}Y avg ${avg == null ? '—' : (avg * 100).toFixed(2)}`;
  })
  .join(' · ');

/** True when `series` has an observation at or before `ts`. Later points do not count. */
const hasObservationAtOrBefore = (series, ts) => (
  (series || []).some((point) => point && point[0] != null && point[0] <= ts)
);

/**
 * True when `series` has points but every one of them is LATER than `ts`, so
 * `stepJoin` would hand back its earliest point — a future print written
 * backward. An empty series is NOT this case: `stepJoin` yields null there,
 * which the callers already treat as a gap.
 */
const borrowsFutureObservation = (series, ts) => (
  !!series?.length && !hasObservationAtOrBefore(series, ts)
);

/**
 * Latest finite value at or before `ts` (step-hold forward). A fiscal year
 * before the first observation is null — `stepJoin` would otherwise fall
 * back to the earliest later point, and this path does not.
 */
const joinedNumber = (series, ts) => {
  if (!series?.length || ts == null || !hasObservationAtOrBefore(series, ts)) return null;
  const value = stepJoin([[ts, null]], series)[0][1];
  if (!isFiniteNumber(value)) return null;
  return value;
};

/**
 * Price timestamp `buildActualSeries` would hold at `ts`: the latest price
 * at or before `ts`. Null when every price is later — do not return price[0].
 */
const priceTimestampAt = (price, ts) => {
  let sourceTs = null;
  (price || []).forEach((point) => {
    if (!point || point[0] == null) return;
    const t = point[0];
    if (t <= ts && (sourceTs == null || t > sourceTs)) sourceTs = t;
  });
  return sourceTs;
};

/**
 * Company value at a fiscal-year date: market cap (price × shares), or
 * enterprise value (market cap + net debt). No price at or before the year, or
 * a non-positive value, is null — a gap, not 0, and not a later price written
 * backward.
 *
 * The same rule applies to every input, not just price: `stepJoin` falls back
 * to a series' EARLIEST point for any timestamp before it, so a fiscal year
 * before the first share-count print (or, on enterprise value, before the first
 * net-debt print) would otherwise be scored on a later print written backward.
 * Both are gapped instead.
 *
 * Price is the only input read at the price point it holds (`priceTimestampAt`,
 * via `joinedNumber`'s step-hold); shares and net debt are read at the fiscal
 * year end `ts` ITSELF. They are dated on the report date, while price is a
 * daily trading series, so whenever a fiscal year ends on a non-trading day the
 * held price sits one or two days earlier than the share/net-debt print dated
 * exactly on `ts` (a Dec-31 on a weekend, or any 52/53-week retailer closing on
 * a Saturday). Measuring shares against that earlier price timestamp would read
 * a same-year print as a future one and gap the whole year. `netDebtAtValue`
 * reads the same `ts`, so the leverage ratio's numerator and denominator agree
 * about which observations exist.
 */
export const companyValueAt = (price, shares, netDebt, valueBasis, ts) => {
  const priceValue = joinedNumber(price, ts);
  const shareCount = joinedNumber(shares, ts);
  if (priceValue == null || shareCount == null) return null;
  if (valueBasis === 'enterpriseValue' && borrowsFutureObservation(netDebt, ts)) return null;
  // No net-debt series at all keeps the old behaviour of market cap (null added
  // as 0); a series whose prints are all later than `ts` is the gap above.
  const debt = valueBasis === 'enterpriseValue' ? (joinedNumber(netDebt, ts) ?? 0) : 0;
  const value = priceValue * shareCount + debt;
  if (!isFiniteNumber(value) || !(value > 0)) return null;
  return value;
};

/**
 * Net debt inside that company value: the debt printed at the fiscal year end,
 * the same observation `companyValueAt` adds into enterprise value, so the
 * leverage ratio's numerator matches its denominator. Gated on a price existing
 * at or before the year, since there is no company value without one. Null
 * stays null (not coerced to 0).
 */
export const netDebtAtValue = (price, netDebt, ts) => {
  if (priceTimestampAt(price, ts) == null) return null;
  return joinedNumber(netDebt, ts);
};

const FY_MATCH_MS = 10 * 24 * 60 * 60 * 1000;

/**
 * Value of `series` at a fiscal-year timestamp. An exact timestamp wins,
 * including an explicit null (a missing year, not a nearby fill). Otherwise
 * the nearest finite point within 10 days — wide enough for a date-rounding
 * slip, narrow enough that a TTM bridge a quarter later is not a fiscal year.
 */
export const sampleNear = (series, ts) => {
  if (!series?.length || ts == null) return null;
  let exact = null;
  let foundExact = false;
  let best = null;
  let bestDist = Infinity;
  series.forEach((point) => {
    if (!point || point[0] == null) return;
    const [t, value] = point;
    if (t === ts) {
      exact = value;
      foundExact = true;
    }
    if (value == null || !isFiniteNumber(value)) return;
    const dist = Math.abs(t - ts);
    if (dist < bestDist) {
      bestDist = dist;
      best = value;
    }
  });
  if (foundExact) return isFiniteNumber(exact) ? exact : null;
  if (bestDist > FY_MATCH_MS) return null;
  return best;
};

const MIN_YEAR_GAP_MS = 270 * 24 * 60 * 60 * 1000;

/** Fallback fiscal-year spine when the income statement isn't on the payload. */
const fiscalYearsFromSeries = (series, lastPriceTs) => {
  const filtered = dropAfterLastPrice(series, lastPriceTs)
    .filter((point) => isFiniteNumber(point[1]))
    .sort((a, b) => a[0] - b[0]);
  const kept = [];
  filtered.forEach(([t]) => {
    if (!kept.length || t - kept[kept.length - 1].x >= MIN_YEAR_GAP_MS) {
      kept.push({ year: fiscalYearNumber(null, t), x: t, row: null });
    }
  });
  return kept;
};

const consecutive = (years, index) => (
  index > 0 && years[index].year === years[index - 1].year + 1
);

/**
 * Annual scores and the rates behind them, in fractions.
 *
 * `valueBasis` moves company value, which since 2026-10-29 drives exactly ONE
 * thing: the "Net debt / company value" UNDERLYING display line
 * (`netDebtToValue`) and its label. It no longer moves the LEVERAGE PILLAR —
 * that pillar switched to FCF coverage (SCORE_CARDS.md §1.6) and takes no
 * price input — and it no longer gates whether a year scores at all: the
 * `row.companyValue == null` gate that used to drop a whole year's quality
 * point was removed once nothing in the pillars read a company value. Before
 * that removal, flipping the toggle to Enterprise Value could still make
 * quality points disappear (`companyValueAt` returns null under
 * `enterpriseValue` for a net-debt series whose prints are all later than the
 * year), which contradicted the "no price input" claim. The quality total is
 * now genuinely unaffected by the Market Cap / Enterprise Value toggle.
 * `valueBasis` never moved net margin, ROA, ROIC, or revenue growth, before or
 * after any of this.
 *
 * ONE price dependency remains, and it is not the toggle's. `netDebtAtValue`
 * returns null unless a price exists at or before the fiscal year, so a year
 * with no price gets a null `netDebt` and therefore a null LEVERAGE pillar.
 * The year still scores — on the other four pillars — rather than vanishing,
 * which is the behaviour the removed gate was hiding. (The dependency is a
 * deliberate "numerator and denominator agree about which observations exist"
 * rule inherited from the old price-derived pillar; narrowing it is a separate
 * question, not a copy fix.)
 *
 * `displayScale` is presentation only: it moves the plotted `revenueGrowth`
 * line and nothing else. The QUALITY score is computed on the TOTAL-company
 * basis whichever way it is set. Per-share growth has already netted out
 * dilution, and dilution has its own dedicated pillar, so feeding per-share
 * growth into the quality pillars charged the same dilution twice and made the
 * score swing with a display toggle. Do not re-wire `displayScale` into the
 * quality math.
 *
 * Rule of 40 is emitted twice instead, on a total and a per-share revenue
 * basis, as two independently named series rather than one toggle-driven line:
 * the per-share lens is growth net of dilution, which is a deliberate second
 * reading and not the definition of the metric.
 *
 * Forward estimates. `years` stays the REPORTED fiscal-year spine and every
 * reported point is computed exactly as before; `forwardYears` is a separate
 * spine whose points are appended to the series the payload can project on the
 * SAME basis as their reported history: both revenue-growth lines (analyst
 * revenue consensus), the EBITDA margin (held at its last reported level — the
 * EBITDA consensus is an adjusted figure on a different basis, see below) and,
 * from those two, both Rule-of-40 readings and both growth scores. Quality, net
 * margin, share-count growth, net debt / company value and FCF growth get no
 * forward point.
 *
 * Operating-margin guard and leverage FCF-smoothing (2026-10-30, mirrors
 * funviz-amplify's `houseScores.mjs` byte-for-byte — SCORE_CARDS.md §1.6).
 * Every per-fiscal-year profitability input goes through
 * `operatingMarginGuardedMargin` before `profitabilityPillar` sees it; the
 * plotted `netMargin` UNDERLYING line stays on the raw, unguarded value —
 * the guard is a score input only. Every per-fiscal-year leverage input is
 * the 3-year mean FCF of the 3 most recent consecutive fiscal years ending
 * there when that window is fully available, falling back to the
 * single-year FCF otherwise (today's pre-2026-10-30 behaviour); the window
 * is fixed at 3 regardless of which trailing-average headline (3Y/5Y/10Y) is
 * later built from the resulting per-year score.
 *
 * The plotted `quality` series is ALSO new as of 2026-10-30: each point is
 * the trailing 3-year average of the (guarded, smoothed) per-year raw score,
 * not that raw score itself — null wherever the last 3 fiscal years are not
 * 3 consecutive years with a non-null raw score each. The raw score survives
 * on each point's `raw` field (pillars already set this precedent), and the
 * tooltip's 5Y/10Y figures are the strict averages of the per-year scores
 * ending at that point, never of the now-averaged plotted line.
 *
 * Growth (Total)/(Per Share) lines (2026-11-01; per-year rule 2026-11-02).
 * Like Quality, each plotted point is the strict trailing 3-year average of
 * the per-year growth score (`growthYearScore`: above-peak growth plus 0.75 ×
 * that year's margin clamped to −100%..40%, over 0.70, capped by the 15% growth ceiling; OCF
 * margin for a REIT), with the
 * per-year score on `raw` — the screener's growthScoreTotal/PerShare rule.
 * Reported points' tooltips show the 5Y/10Y averages of the reported per-year
 * scores only (the screener's growthScore*5y/10y basis); forward points
 * average across the reported/forward boundary and carry no `.detail`. See
 * `plotGrowth` below. A lender (`isGrowthLender`, 2026-11-03) scores on net
 * revenue with margin 0 and gets no forward Growth points.
 *
 * No TTM bridge point (2026-10-30, removed). A TTM "third spine" between
 * reported fiscal years and forward estimates was tried and then dropped:
 * its Quality reading omitted dilution, the FCF-quality pillar (with no
 * growth/level window to read, a profitable company's fcf pillar scores
 * `null`, not 0) and the revenue-quality pillar, which structurally capped
 * the TTM Quality score at `profitability.max + leverage.max` (0.60 on the
 * 2026-10-31 caps) versus 1.00 for a normal fiscal year — a mechanical dip at every
 * TTM point for any company whose FCF/revenue pillars normally contribute,
 * independent of the company's actual trajectory. Removed rather than
 * patched (see SCORE_CARDS.md §5 for the dated entry).
 */
export const buildAnnualScores = ({
  price = [],
  shares = [],
  netDebt = [],
  valueBasis = 'marketCap',
  displayScale = 'perShare',
  revenue = [],
  ebitda = [],
  fcf = [],
  incomeAnnual = [],
  roa = null,
  roic = null,
  // Raw statement rows the Quality score runs on (lambda.py's `qualityInputs`:
  // { income, cashflow, balance, isFinancial, isReit }, reporting currency,
  // oldest-first). Quality never reads the FX-converted series above.
  qualityInputs = null,
} = {}) => {
  const lastPriceTs = lastNonNullPoint(price)?.[0] ?? null;
  let years = reportedFiscalYears(incomeAnnual, lastPriceTs);
  if (!years.length) {
    years = fiscalYearsFromSeries(revenue, lastPriceTs);
    if (!years.length) years = fiscalYearsFromSeries(fcf, lastPriceTs);
  }

  const reportedRevenue = dropAfterLastPrice(revenue, lastPriceTs);
  // The plotted revenue-growth line follows the page's per-share/total toggle.
  const displayRevenue = scaleTotalSeries(reportedRevenue, displayScale, shares);
  // Quality's inputs are total-company regardless of that toggle (see above).
  // Rule of 40 reads both bases, so both are built unconditionally.
  const totalRevenue = scaleTotalSeries(reportedRevenue, 'total', shares);
  const perShareRevenue = scaleTotalSeries(reportedRevenue, 'perShare', shares);
  // EBITDA and revenue both come from `metrics.*`, so the EBITDA margin's
  // numerator and denominator share one FX basis (see `ebitdaMarginFrom`).
  const totalEbitda = scaleTotalSeries(dropAfterLastPrice(ebitda, lastPriceTs), 'total', shares);
  const totalFcf = scaleTotalSeries(dropAfterLastPrice(fcf, lastPriceTs), 'total', shares);

  // Raw statement rows (reporting currency) for Quality, and for a REIT's
  // Growth margin (OCF / revenue): both come from `qualityInputs`, so the
  // ratio obeys the same-source rule (§1.3).
  const qualityRows = buildScoreRows(qualityInputs || {});
  const isReit = !!qualityInputs?.isReit;
  const ocfMarginByYear = new Map(qualityRows
    .filter((r) => r.year != null)
    .map((r) => [r.year, ocfMarginFrom(r.operatingCashFlow, r.revenue)]));
  // Per-share Growth basis: revenue / income-statement weightedAverageShsOut,
  // both from `qualityInputs` — the screener's revenuePerShare — not revenue
  // over the page's `shares` series (a different share count).
  const revenuePerShareByYear = new Map(qualityRows
    .filter((r) => r.year != null)
    .map((r) => [r.year, isPosFinite(r.shares) && r.revenue != null ? r.revenue / r.shares : null]));
  // Lender Growth basis (see `isGrowthLender`): net revenue and net revenue per
  // share by year, from the same `qualityInputs` rows.
  const lender = isGrowthLender(qualityRows, qualityInputs?.isFinancial, isReit);
  const lenderNetByYear = new Map(lender ? qualityRows
    .filter((r) => r.year != null)
    .map((r) => [r.year, lenderNetRevenue(r)]) : []);
  const lenderNetAt = (year) => lenderNetByYear.get(year) ?? null;
  const lenderNetPerShareByYear = new Map(lender ? qualityRows
    .filter((r) => r.year != null)
    .map((r) => {
      const net = lenderNetRevenue(r);
      return [r.year, isPosFinite(r.shares) && net != null ? net / r.shares : null];
    }) : []);

  const rows = years.map((year) => {
    const margin = netMarginFromRow(year.row);
    const shareCount = joinedNumber(shares, year.x);
    const totalRevenueAt = sampleNear(totalRevenue, year.x);
    const ebitdaAt = sampleNear(totalEbitda, year.x);
    return {
      ...year,
      margin,
      ebitda: ebitdaAt,
      ebitdaMargin: ebitdaMarginFrom(ebitdaAt, totalRevenueAt),
      // The Growth score's margin term: that year's own margin (see GROWTH_MARGIN_CAP).
      growthMarginBasis: isReit ? (ocfMarginByYear.get(year.year) ?? null) : ebitdaMarginFrom(ebitdaAt, totalRevenueAt),
      displayRevenue: sampleNear(displayRevenue, year.x),
      revenue: totalRevenueAt,
      // Page basis (metrics revenue / `shares`): only the forward tail's first
      // growth denominator reads it, since the consensus revenue is on it too.
      perShareRevenue: sampleNear(perShareRevenue, year.x),
      // Reported per-share growth basis (see `revenuePerShareByYear`).
      growthRevenuePerShare: revenuePerShareByYear.get(year.year) ?? null,
      fcf: sampleNear(totalFcf, year.x),
      shares: shareCount,
      netDebt: netDebtAtValue(price, netDebt, year.x),
      companyValue: companyValueAt(price, shares, netDebt, valueBasis, year.x),
      roa: Array.isArray(roa) ? sampleNear(roa, year.x) : null,
      roic: Array.isArray(roic) ? sampleNear(roic, year.x) : null,
    };
  });

  const growthAt = (pick, index) => {
    if (!consecutive(years, index)) return null;
    return yoyGrowth(pick(rows[index]), pick(rows[index - 1]));
  };

  // Growth peak and rising-years input: total revenue per reported fiscal
  // year, extended by the forward tail further down (houseScores.mjs priorPeak,
  // risingYearShare, frozen at the last reported year for forward years).
  const peakRevenue = rows.map((row) => (lender ? lenderNetAt(row.year) : row.revenue));
  const peakYears = years.map((year) => year.year);
  const peakConsecutiveAt = (k) => k > 0 && peakYears[k] != null && peakYears[k] === peakYears[k - 1] + 1;
  // The rising-years share is frozen at the last reported year for forward
  // years: an estimate carries no evidence of how reliable past growth was.
  const lastReportedIndex = rows.length - 1;
  // Per-year growth scores for one fiscal year (0-1 fractions, or null), the
  // screener's computeYearGrowthScores: Total takes the lower of total and
  // per-share growth; both share the total-revenue growth-credit factor.
  const yearGrowthScores = (index, gTotal, gPerShare, margin) => {
    // A non-positive revenue in the year or its base year (exact 0 included)
    // scores 0 on both lines; a missing revenue stays a gap.
    const revenueNow = peakRevenue[index];
    const revenueBase = peakRevenue[index - 1];
    if (peakConsecutiveAt(index) && isFiniteNumber(revenueNow) && isFiniteNumber(revenueBase)
      && (revenueNow <= 0 || revenueBase <= 0)) {
      return { total: 0, perShare: 0 };
    }
    const peak = priorPeak(peakRevenue, peakConsecutiveAt, index);
    if (peak == null) return { total: null, perShare: null };
    const factor = growthPeakFactor(peakRevenue[index], peakRevenue[index - 1], peak)
      * growthTrendFactor(risingYearShare(peakRevenue, peakConsecutiveAt, Math.min(index, lastReportedIndex)));
    const totalGrowth = isFiniteNumber(gTotal) && isFiniteNumber(gPerShare) ? Math.min(gTotal, gPerShare) : gTotal;
    const total = growthYearScore(totalGrowth, factor, margin);
    const perShare = growthYearScore(gPerShare, factor, margin);
    return { total: total == null ? null : total / 100, perShare: perShare == null ? null : perShare / 100 };
  };

  const netMargin = [];
  const ebitdaMargin = [];
  const revenueGrowth = [];
  const revenueGrowthTotal = [];
  const revenueGrowthPerShare = [];
  const shareGrowth = [];
  const netDebtToValue = [];
  const fcfGrowth = [];
  const roaPoints = [];
  const roicPoints = [];
  const ruleOf40Total = [];
  const ruleOf40PerShare = [];
  // Per-year growth scores (fractions), reported years then forward years;
  // the plotted `growthScoreTotal`/`growthScorePerShare` points are built
  // from these further down.
  const growthRawTotal = [];
  const growthRawPerShare = [];

  rows.forEach((row, index) => {
    const at = (y) => ({ x: row.x, year: row.year, y });
    if (isFiniteNumber(row.margin)) netMargin.push(at(row.margin));
    if (isFiniteNumber(row.ebitdaMargin)) ebitdaMargin.push(at(row.ebitdaMargin));
    const revGrowth = growthAt((item) => item.displayRevenue, index);
    if (isFiniteNumber(revGrowth)) revenueGrowth.push(at(revGrowth));
    const revGrowthTotal = growthAt((item) => item.revenue, index);
    if (isFiniteNumber(revGrowthTotal)) revenueGrowthTotal.push(at(revGrowthTotal));
    const revGrowthPerShare = growthAt((item) => item.growthRevenuePerShare, index);
    if (isFiniteNumber(revGrowthPerShare)) revenueGrowthPerShare.push(at(revGrowthPerShare));
    const shareChange = growthAt((item) => item.shares, index);
    if (isFiniteNumber(shareChange)) shareGrowth.push(at(shareChange));
    const fcfChange = growthAt((item) => item.fcf, index);
    if (isFiniteNumber(fcfChange)) fcfGrowth.push(at(fcfChange));
    // The true ratio, negative for net cash — an underlying line must not hide the sign.
    if (isFiniteNumber(row.netDebt) && isFiniteNumber(row.companyValue) && row.companyValue > 0) {
      netDebtToValue.push(at(row.netDebt / row.companyValue));
    }
    if (isFiniteNumber(row.roa)) roaPoints.push(at(row.roa));
    if (isFiniteNumber(row.roic)) roicPoints.push(at(row.roic));

    // Classic Rule of 40 (that year's Growth margin basis + YoY revenue
    // growth) on both revenue bases, for reference; not what the score plots.
    const r40Total = ruleOf40(row.growthMarginBasis, revGrowthTotal);
    if (isFiniteNumber(r40Total)) ruleOf40Total.push(at(r40Total));
    const r40PerShare = ruleOf40(row.growthMarginBasis, revGrowthPerShare);
    if (isFiniteNumber(r40PerShare)) ruleOf40PerShare.push(at(r40PerShare));
    // The Growth score, per year. Revenue and base revenue ride along for
    // the plotted average's non-positive-revenue cap.
    // A lender scores on net revenue with margin 0; the plotted growth and
    // Rule-of-40 reference lines above stay on reported revenue.
    const scored = lender
      ? yearGrowthScores(
        index,
        growthAt((item) => lenderNetAt(item.year), index),
        growthAt((item) => lenderNetPerShareByYear.get(item.year) ?? null, index),
        0,
      )
      : yearGrowthScores(index, revGrowthTotal, revGrowthPerShare, row.growthMarginBasis);
    const revenueContext = { revenue: peakRevenue[index], baseRevenue: index > 0 ? peakRevenue[index - 1] : null };
    if (isFiniteNumber(scored.total)) growthRawTotal.push({ ...at(scored.total), ...revenueContext });
    if (isFiniteNumber(scored.perShare)) growthRawPerShare.push({ ...at(scored.perShare), ...revenueContext });
  });

  // Quality: the screener's per-year engine over the raw statement rows. The
  // plotted line is the strict trailing 3-year average (null unless the last
  // 3 fiscal years are consecutive and each scores); the tooltip shows the
  // strict 5Y/10Y averages ending at that point. 0-100 arithmetic, identical
  // to houseScores.mjs; only the plotted value is divided by 100 (chart unit).
  const qualityCtx = qualityContext(qualityInputs);
  const quality = [];
  qualityRows.forEach((row, i) => {
    const scored = computeYearQualityPillars(qualityRows, i, qualityCtx);
    if (!isFiniteNumber(scored?.fraction)) return;
    const threeYear = averageQualityWindow(qualityRows, i, QUALITY_AVERAGE_WINDOWS.qualityScore, qualityCtx);
    const averages = [QUALITY_AVERAGE_WINDOWS.qualityScore5y, QUALITY_AVERAGE_WINDOWS.qualityScore10y]
      .map((n) => {
        const avg = averageQualityWindow(qualityRows, i, n, qualityCtx);
        return `${n}Y avg ${avg == null ? '—' : avg.toFixed(2)}`;
      });
    quality.push({
      x: row.x,
      year: row.year,
      y: isFiniteNumber(threeYear) ? threeYear / 100 : null,
      pillars: scored.pillars,
      raw: scored.fraction,
      detail: averages.join(' · '),
    });
  });

  // Forward estimates. Sampled from the UNDROPPED `revenue`/`ebitda` props —
  // the reported path above keeps using `dropAfterLastPrice` untouched. The
  // first forward year's growth denominator is the last reported FISCAL YEAR.
  const lastReported = years.length >= 2 ? rows[rows.length - 1] : null;
  const forwardYears = forwardFiscalYears(revenue, lastPriceTs, lastReported);
  if (forwardYears.length) {
    const forwardTotalRevenue = scaleTotalSeries(revenue, 'total', shares);
    const forwardPerShareRevenue = scaleTotalSeries(revenue, 'perShare', shares);
    // The margin term is HELD AT ITS LAST REPORTED LEVEL, not taken from the
    // EBITDA consensus. The reported years' `ebitda` is the provider's
    // GAAP-derived field (EBIT + D&A, stock comp still expensed) while
    // `ebitdaAvg` is an analyst ADJUSTED EBITDA that usually strips stock comp,
    // so splicing them steps the line by the size of that adjustment rather
    // than by anything economic — CRWD FY2026 reads 3.8% reported against 33.6%
    // on the consensus basis for the SAME year, and LLY FY2025 goes the other
    // way (48.6% vs 43.5%), so it is not a correctable constant either. Holding
    // the reported margin flat keeps the whole line on one basis and leaves the
    // forward movement to revenue growth, which is the term whose consensus IS
    // on the reported basis (consensus revenue ≈ reported revenue).
    // The plotted EBITDA-margin line holds its own last value; the score holds
    // the last reported year's Growth margin term (OCF for a REIT).
    const heldMargin = lastReported?.ebitdaMargin ?? null;
    const heldGrowthMargin = lastReported?.growthMarginBasis ?? null;
    let prior = lastReported;
    forwardYears.forEach(({ year, x }) => {
      const at = (y) => ({ x, year, y });
      const total = sampleNear(forwardTotalRevenue, x);
      const perShare = sampleNear(forwardPerShareRevenue, x);
      const gTotal = yoyGrowth(total, prior?.revenue);
      const gPerShare = yoyGrowth(perShare, prior?.perShareRevenue);
      if (isFiniteNumber(heldMargin)) ebitdaMargin.push(at(heldMargin));
      if (isFiniteNumber(gTotal)) revenueGrowthTotal.push(at(gTotal));
      if (isFiniteNumber(gPerShare)) revenueGrowthPerShare.push(at(gPerShare));
      const r40Total = ruleOf40(heldGrowthMargin, gTotal);
      if (isFiniteNumber(r40Total)) ruleOf40Total.push(at(r40Total));
      const r40PerShare = ruleOf40(heldGrowthMargin, gPerShare);
      if (isFiniteNumber(r40PerShare)) ruleOf40PerShare.push(at(r40PerShare));
      // A lender gets no forward Growth point: the consensus is gross revenue,
      // not net of interest expense.
      if (lender) {
        prior = { revenue: total, perShareRevenue: perShare };
        return;
      }
      // Same per-year rule as the reported years; the peak window runs over
      // reported and earlier forward revenue alike, while the rising-years
      // share stays at its last reported value.
      peakRevenue.push(total);
      peakYears.push(year);
      const scored = yearGrowthScores(peakRevenue.length - 1, gTotal, gPerShare, heldGrowthMargin);
      const revenueContext = { revenue: total, baseRevenue: prior?.revenue ?? null, forward: true };
      if (isFiniteNumber(scored.total)) growthRawTotal.push({ ...at(scored.total), ...revenueContext });
      if (isFiniteNumber(scored.perShare)) growthRawPerShare.push({ ...at(scored.perShare), ...revenueContext });
      prior = { revenue: total, perShareRevenue: perShare };
    });
  }

  // Plotted Growth lines (2026-11-01): each point is the strict trailing
  // 3-year average of the per-year scores ending there (null unless the last 3
  // scored years are consecutive) — the screener's growthScoreTotal/PerShare
  // rule, and the same shape as the Quality line. `raw` keeps the per-year
  // score. Reported points carry the 5Y/10Y tooltip averages of the reported
  // per-year scores only (the screener's growthScore*5y/10y basis); forward
  // points average across the reported/forward boundary so the line stays
  // continuous, and carry no `.detail`.
  const plotGrowth = (raw) => raw.map((point, i) => {
    const window = raw.slice(0, i + 1);
    const plotted = {
      x: point.x,
      year: point.year,
      y: trailingGrowthAverage(window, GROWTH_HEADLINE_WINDOW),
      raw: point.y,
    };
    if (!point.forward) plotted.detail = formatGrowthAverages(window);
    return plotted;
  });
  const growthScoreTotal = plotGrowth(growthRawTotal);
  const growthScorePerShare = plotGrowth(growthRawPerShare);

  return {
    years,
    forwardYears,
    netMargin,
    ebitdaMargin,
    revenueGrowth,
    revenueGrowthTotal,
    revenueGrowthPerShare,
    shareGrowth,
    netDebtToValue,
    fcfGrowth,
    roa: roaPoints,
    roic: roicPoints,
    quality,
    ruleOf40Total,
    ruleOf40PerShare,
    growthScoreTotal,
    growthScorePerShare,
  };
};
